import React, { useState, useEffect } from 'react';
import { Save as SaveIcon, FileOutput, Loader2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Save bar that sits above calculator inputs. Mirrors the estimate
 * editor's Save UX — button goes teal/dark when state is saved; turns
 * outline/light when the user mutates any input.
 *
 * Props come straight from useCalculatorSave.
 */
export default function CalculatorSaveBar({
  name, setName,
  estimateId, setEstimateId,
  isSaved, isDirty, saving,
  save, pushToEstimate,
}) {
  const [estimates, setEstimates] = useState([]);
  const [pushOpen, setPushOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/estimates`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setEstimates)
      .catch(() => {});
  }, []);

  const attachedEstimate = estimates.find(e => e.estimate_id === estimateId) || null;

  return (
    <div className="bg-white border border-[#E6EBE8] rounded-md p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="calc-save-bar">
      <div className="flex-1 min-w-[220px] flex items-center gap-2">
        <label className="text-xs uppercase tracking-[0.15em] text-[#6B7A73] font-semibold whitespace-nowrap">Name</label>
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Front walkway — Deslongchamps"
          className="bg-white border-[#E6EBE8] rounded-md"
          data-testid="calc-name-input"
        />
      </div>

      <div className="flex items-center gap-2">
        <label className="text-xs uppercase tracking-[0.15em] text-[#6B7A73] font-semibold whitespace-nowrap">Estimate</label>
        <Select
          value={estimateId || '__none__'}
          onValueChange={v => setEstimateId(v === '__none__' ? null : v)}
        >
          <SelectTrigger className="w-48 bg-white border-[#E6EBE8] rounded-md text-sm" data-testid="calc-estimate-select">
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {estimates.map(e => (
              <SelectItem key={e.estimate_id} value={e.estimate_id}>
                {e.estimate_number} · {e.title?.slice(0, 32) || 'Untitled'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        onClick={() => setPushOpen(true)}
        variant="outline"
        className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md"
        data-testid="calc-push-btn"
      >
        <FileOutput className="w-4 h-4 mr-2" /> Push to Estimate
      </Button>

      <Button
        onClick={save}
        disabled={saving}
        className={isSaved
          ? 'bg-[#2D6B68] border border-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-4 py-2 font-medium'
          : 'bg-white border border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium'
        }
        data-testid="calc-save-btn"
      >
        {saving
          ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          : isSaved
            ? <Check className="w-4 h-4 mr-2" />
            : <SaveIcon className="w-4 h-4 mr-2" />}
        {saving ? 'Saving…' : isSaved ? 'Saved' : (isDirty ? 'Save' : 'Save')}
      </Button>

      <PushToEstimateDialog
        open={pushOpen}
        onOpenChange={setPushOpen}
        estimates={estimates}
        initialTarget={estimateId}
        defaultName={name}
        onConfirm={async ({ mode, target, newTitle }) => {
          const res = await pushToEstimate({
            target: mode === 'existing' ? target : null,
            newTitle: mode === 'new' ? newTitle : null,
          });
          if (res) setPushOpen(false);
        }}
        attachedEstimate={attachedEstimate}
      />
    </div>
  );
}

function PushToEstimateDialog({
  open, onOpenChange, estimates, initialTarget, defaultName, onConfirm, attachedEstimate,
}) {
  const [mode, setMode] = useState(initialTarget ? 'existing' : 'new');
  const [target, setTarget] = useState(initialTarget || '');
  const [newTitle, setNewTitle] = useState(defaultName || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialTarget ? 'existing' : 'new');
      setTarget(initialTarget || (estimates[0]?.estimate_id || ''));
      setNewTitle(defaultName || '');
    }
  }, [open, initialTarget, defaultName, estimates]);

  const go = async () => {
    setBusy(true);
    try { await onConfirm({ mode, target, newTitle }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-md">
        <DialogHeader>
          <DialogTitle>Push calculation to estimate</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {attachedEstimate && (
            <p className="text-xs text-[#6B7A73]">
              Currently attached to <strong>{attachedEstimate.estimate_number}</strong>.
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setMode('existing')}
              className={`flex-1 px-3 py-2 text-sm rounded-md border ${
                mode === 'existing'
                  ? 'border-[#2D6B68] bg-[#E5F0EE] text-[#1A1D1C]'
                  : 'border-[#E6EBE8] text-[#6B7A73] hover:bg-[#F0F3F1]'
              }`}
              data-testid="push-mode-existing"
            >
              Add to existing
            </button>
            <button
              onClick={() => setMode('new')}
              className={`flex-1 px-3 py-2 text-sm rounded-md border ${
                mode === 'new'
                  ? 'border-[#2D6B68] bg-[#E5F0EE] text-[#1A1D1C]'
                  : 'border-[#E6EBE8] text-[#6B7A73] hover:bg-[#F0F3F1]'
              }`}
              data-testid="push-mode-new"
            >
              Create new estimate
            </button>
          </div>

          {mode === 'existing' ? (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-[#6B7A73] font-semibold">Target estimate</label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="bg-white border-[#E6EBE8] rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {estimates.map(e => (
                    <SelectItem key={e.estimate_id} value={e.estimate_id}>
                      {e.estimate_number} · {e.title?.slice(0, 40) || 'Untitled'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B7A73]">The calculation will be appended as a new section.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-[#6B7A73] font-semibold">New estimate title</label>
              <Input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="e.g. Front walkway pour"
                className="bg-white border-[#E6EBE8] rounded-md"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">
            Cancel
          </Button>
          <Button
            onClick={go}
            disabled={busy || (mode === 'existing' && !target)}
            className="bg-[#2D6B68] text-white hover:bg-[#235552]"
            data-testid="push-confirm-btn"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === 'new' ? 'Create estimate' : 'Append section'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
